/**
 * A round zone as a GeoJSON polygon.
 *
 * Producers mark a zone by standing at its centre and picking a radius (or,
 * on the web, by clicking a centre and an edge). Zones are stored and checked
 * as polygons everywhere — the DB, the server-side ping gate and both maps —
 * so a "circle" has to become a polygon before it is saved. Lifted from the
 * web zone editor (LiveTrackingSetup) so the web app and the native producer
 * app draw the same shape for the same centre and radius.
 */

import type { LatLng } from "../polygonCentroid";

/** A GeoJSON Polygon geometry: one closed outer ring of [lng, lat] positions. */
export interface CirclePolygonGeometry {
  type: "Polygon";
  coordinates: number[][][];
}

/**
 * Enough vertices that the edge looks round on a map at festival zoom
 * levels, few enough that a stored zone stays small.
 */
export const DEFAULT_CIRCLE_POINT_COUNT = 64;

/**
 * Metres per degree of longitude at the equator, and per degree of
 * latitude. Kept exactly as the web editor had them so a zone redrawn here
 * matches one drawn there; the flat approximation is well under 1% off for
 * the few-hundred-metre radii a festival zone has.
 */
const METERS_PER_DEGREE_LNG_AT_EQUATOR = 111320;
const METERS_PER_DEGREE_LAT = 110574;

/**
 * Build a closed polygon approximating a circle around `center`.
 *
 * The ring has `pointCount` vertices evenly spaced counter-clockwise from due
 * east, followed by a repeat of the first vertex (GeoJSON rings must be
 * closed), so it holds `pointCount + 1` positions.
 *
 * Invalid input throws a RangeError rather than being clamped: a radius of
 * zero or less has no circle to draw (a degenerate ring would be saved as a
 * zone nobody can ever be inside), and fewer than three vertices is not a
 * polygon. Silently substituting another shape would hide the caller's bug —
 * e.g. a GPS editor that hasn't moved from the centre yet should ask the
 * producer to walk to the edge, not save a dot.
 */
export function createCirclePolygon(
  center: LatLng,
  radiusInMeters: number,
  pointCount: number = DEFAULT_CIRCLE_POINT_COUNT,
): CirclePolygonGeometry {
  if (!Number.isFinite(radiusInMeters) || radiusInMeters <= 0) {
    throw new RangeError(
      `Circle radius must be a positive number of metres, got ${radiusInMeters}`,
    );
  }
  if (!Number.isInteger(pointCount) || pointCount < 3) {
    throw new RangeError(`Circle needs a whole number of at least 3 points, got ${pointCount}`);
  }

  const ring: number[][] = [];
  for (let i = 0; i < pointCount; i++) {
    const theta = (i / pointCount) * (2 * Math.PI);
    const vertex = pointAtBearing(center, radiusInMeters, theta);
    ring.push([vertex.lng, vertex.lat]);
  }

  // Close the ring with a copy rather than the same array (the web original
  // reused it): code that shifts every vertex in place — dragging the zone,
  // say — would otherwise move the first vertex twice and open the ring.
  ring.push([ring[0][0], ring[0][1]]);

  return { type: "Polygon", coordinates: [ring] };
}

// ─────────────────────────────────────────────────────────────────────────────
// The same flat metre grid, for editors that move a centre or an edge
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Metres per degree of longitude at `lat`. A degree of longitude shrinks
 * towards the poles; a degree of latitude doesn't (in this approximation),
 * so the two axes scale differently.
 */
function metersPerDegreeLng(lat: number): number {
  return METERS_PER_DEGREE_LNG_AT_EQUATOR * Math.cos((lat * Math.PI) / 180);
}

/** `center` moved `eastMeters` east and `northMeters` north, on the circle's grid. */
export function offsetLatLng(center: LatLng, eastMeters: number, northMeters: number): LatLng {
  return {
    lng: center.lng + eastMeters / metersPerDegreeLng(center.lat),
    lat: center.lat + northMeters / METERS_PER_DEGREE_LAT,
  };
}

/**
 * The point `radiusInMeters` from `center` in direction `bearing` (radians,
 * counter-clockwise from due east — the angle createCirclePolygon walks).
 * It lies exactly on that circle's ring, so an editor's edge handle sits on
 * the outline it drags.
 */
export function pointAtBearing(center: LatLng, radiusInMeters: number, bearing: number): LatLng {
  return offsetLatLng(
    center,
    radiusInMeters * Math.cos(bearing),
    radiusInMeters * Math.sin(bearing),
  );
}

/**
 * Distance and direction from `center` to `point` on the same grid — the
 * exact inverse of pointAtBearing, so dragging an edge handle to a point
 * gives the circle that passes through it. Measured at the centre's
 * latitude; well under 1% off great-circle distance at festival scale.
 */
export function flatOffsetMeters(
  center: LatLng,
  point: LatLng,
): { meters: number; bearing: number } {
  const [east, north] = flatXYMeters(center, point);
  return { meters: Math.hypot(east, north), bearing: Math.atan2(north, east) };
}

/**
 * `point` as [east, north] metres from `origin` on the same grid — the
 * inverse of offsetLatLng, for planar maths (segment distances) near it.
 */
export function flatXYMeters(origin: LatLng, point: LatLng): [number, number] {
  return [
    (point.lng - origin.lng) * metersPerDegreeLng(origin.lat),
    (point.lat - origin.lat) * METERS_PER_DEGREE_LAT,
  ];
}
